<template>
  <div class="global-smart-search">
    <button class="search-trigger" @click="openModal" :title="isMac ? '⌘K' : 'Ctrl+K'">
      <feather-icon icon="SearchIcon" size="18" />
      <span class="search-label">Buscar...</span>
      <kbd class="keyboard-hint">{{ isMac ? '⌘K' : 'Ctrl+K' }}</kbd>
    </button>

    <portal to="modal-portal">
      <transition name="modal-fade">
        <div v-if="isOpen" class="search-modal-overlay" @click.self="closeModal">
          <div class="search-modal" @keydown="handleKeydown">
          <div class="search-header">
            <div class="search-input-wrapper">
              <feather-icon icon="SearchIcon" size="18" class="search-icon" />
              <input
                ref="searchInput"
                type="text"
                class="search-input"
                placeholder="Buscar..."
                v-model="query"
                @input="onInput"
                autocomplete="off"
              />
              <div v-if="isLoading" class="loading-spinner">
                <b-spinner small variant="primary" />
              </div>
              <button v-if="query" class="clear-btn" @click="clearSearch">
                <feather-icon icon="XIcon" size="16" />
              </button>
            </div>
            <button class="close-btn" @click="closeModal">
              <feather-icon icon="XIcon" size="20" />
            </button>
          </div>

          <div class="search-body">
            <div class="categories-sidebar">
              <button
                class="category-item"
                :class="{ active: selectedCategory === 'all' }"
                @click="selectCategory('all')"
              >
                <feather-icon icon="GridIcon" size="16" />
                <span>Todas as categorias</span>
                <span class="category-count">{{ totalResults }}</span>
              </button>

              <div class="category-divider"></div>

              <button
                v-for="cat in categoriesWithResults"
                :key="cat.key"
                class="category-item"
                :class="{ active: selectedCategory === cat.key }"
                @click="selectCategory(cat.key)"
              >
                <feather-icon :icon="cat.icon" size="16" />
                <span>{{ cat.label }}</span>
                <span class="category-count">{{ cat.count }}</span>
              </button>
            </div>

            <div class="results-panel">
              <div v-if="isLoading && !hasResults" class="results-loading">
                <div class="skeleton-group" v-for="n in 3" :key="n">
                  <div class="skeleton-header"></div>
                  <div class="skeleton-item" v-for="m in 2" :key="m"></div>
                </div>
              </div>

              <div v-else-if="!hasResults && query.length >= 2 && !isLoading" class="no-results">
                <feather-icon icon="SearchIcon" size="40" class="no-results-icon" />
                <h4>Nenhum resultado encontrado</h4>
                <p>Tente buscar por outro termo ou verifique a ortografia.</p>
              </div>

              <div v-else-if="query.length < 2" class="search-hint">
                <feather-icon icon="InfoIcon" size="24" class="hint-icon" />
                <p>Digite pelo menos 2 caracteres para iniciar a busca</p>
                <div class="hint-examples">
                  <span>Exemplos: Nome do cliente, número do pedido, CNPJ</span>
                </div>
              </div>

              <template v-else>
                <div
                  v-for="group in filteredResults"
                  :key="group.key"
                  class="result-group"
                >
                  <div class="group-header">
                    <feather-icon :icon="group.icon" size="14" />
                    <span>{{ group.label }}</span>
                    <span class="group-count">{{ group.items.length }}</span>
                  </div>

                  <div
                    v-for="(item, index) in group.items.slice(0, expandedGroups[group.key] ? 20 : 5)"
                    :key="`${group.key}-${index}`"
                    class="result-item"
                    :class="{ 'is-highlighted': isHighlighted(group.key, index) }"
                    @click="selectItem(group.key, item)"
                    @mouseenter="setHighlight(group.key, index)"
                  >
                    <div class="item-icon">
                      <feather-icon :icon="group.icon" size="16" />
                    </div>
                    <div class="item-content">
                      <div class="item-title" v-html="highlightMatch(item.title)"></div>
                      <div class="item-subtitle" v-html="highlightMatch(item.subtitle)"></div>
                    </div>
                    <div class="item-meta">
                      <span v-if="item.status" class="item-status" :class="getStatusClass(item.status)">
                        {{ item.status }}
                      </span>
                      <span v-if="item.value" class="item-value">{{ formatCurrency(item.value) }}</span>
                    </div>
                    <feather-icon icon="ChevronRightIcon" size="16" class="item-arrow" />
                  </div>

                  <button
                    v-if="group.items.length > 5 && !expandedGroups[group.key]"
                    class="see-more-btn"
                    @click="toggleExpand(group.key)"
                  >
                    Ver mais {{ group.items.length - 5 }} resultados
                  </button>
                  <button
                    v-else-if="expandedGroups[group.key]"
                    class="see-more-btn"
                    @click="toggleExpand(group.key)"
                  >
                    Ver menos
                  </button>
                </div>
              </template>
            </div>
          </div>

          <div class="search-footer">
            <div class="footer-hints">
              <span class="hint">
                <kbd>↑</kbd><kbd>↓</kbd> navegar
              </span>
              <span class="hint">
                <kbd>Enter</kbd> selecionar
              </span>
              <span class="hint">
                <kbd>Esc</kbd> fechar
              </span>
            </div>
          </div>
          </div>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script>
import axios from '@/libs/axios';
import { Portal } from 'portal-vue';

export default {
  name: 'GlobalSmartSearch',
  components: {
    Portal,
  },
  data() {
    return {
      isOpen: false,
      query: '',
      isLoading: false,
      debounceTimer: null,
      highlightedGroup: null,
      highlightedIndex: 0,
      selectedCategory: 'all',
      expandedGroups: {},
      results: {
        clientes: { items: [], total: 0 },
        pedidos: { items: [], total: 0 },
        rascunhos: { items: [], total: 0 },
        // pics: { items: [], total: 0 },
        produtos: { items: [], total: 0 },
        representantes: { items: [], total: 0 },
      },
    };
  },
  computed: {
    isMac() {
      return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    },
    userProfileName() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
        return perfilLogado?.perfil_acesso_nome || null;
      } catch (error) {
        return null;
      }
    },
    restrictedCategories() {
      if (this.userProfileName === 'ClienteFinal') return ['clientes', 'representantes'];
      if (this.userProfileName === 'Representante') return ['representantes'];
      return [];
    },
    categories() {
      const baseCategories = [
        { key: 'clientes', label: 'Clientes', icon: 'UsersIcon' },
        { key: 'pedidos', label: 'Pedidos', icon: 'PackageIcon' },
        { key: 'rascunhos', label: 'Rascunhos', icon: 'FileTextIcon' },
        // { key: 'pics', label: 'PICs', icon: 'ClipboardIcon' },
        { key: 'produtos', label: 'Produtos', icon: 'BoxIcon' },
        { key: 'representantes', label: 'Representantes', icon: 'UserCheckIcon' },
      ];

      if (this.restrictedCategories.length === 0) return baseCategories;

      return baseCategories.filter((cat) => !this.restrictedCategories.includes(cat.key));
    },
    categoriesWithResults() {
      return this.categories
        .map((cat) => ({
          ...cat,
          count: this.results[cat.key]?.items?.length || 0,
        }))
        .filter((cat) => cat.count > 0);
    },
    totalResults() {
      return this.categoriesWithResults.reduce((sum, cat) => sum + cat.count, 0);
    },
    hasResults() {
      return this.totalResults > 0;
    },
    filteredResults() {
      const groups = [];

      for (const cat of this.categories) {
        if (this.selectedCategory !== 'all' && this.selectedCategory !== cat.key) continue;

        const items = this.results[cat.key]?.items || [];
        if (items.length === 0) continue;

        groups.push({
          key: cat.key,
          label: cat.label,
          icon: cat.icon,
          items: this.transformItems(cat.key, items),
        });
      }

      return groups;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    handleGlobalKeydown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        this.openModal();
      }
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.closeModal();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.navigateDown();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateUp();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.selectCurrent();
      }
    },
    openModal() {
      this.isOpen = true;
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    closeModal() {
      this.isOpen = false;
      this.query = '';
      this.selectedCategory = 'all';
      this.expandedGroups = {};
      this.resetResults();
    },
    resetResults() {
      this.results = {
        clientes: { items: [], total: 0 },
        pedidos: { items: [], total: 0 },
        rascunhos: { items: [], total: 0 },
        // pics: { items: [], total: 0 },
        produtos: { items: [], total: 0 },
        representantes: { items: [], total: 0 },
      };
    },
    clearSearch() {
      this.query = '';
      this.resetResults();
      this.$refs.searchInput?.focus();
    },
    onInput() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);

      if (this.query.length < 2) {
        this.resetResults();
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.performSearch();
      }, 350);
    },
    async performSearch() {
      if (this.query.length < 2) return;

      this.isLoading = true;

      try {
        const response = await axios.get('/search/global', {
          params: { q: this.query },
        });

        if (response.data?.status === 'success') {
          this.results = response.data.data;
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        this.isLoading = false;
      }
    },
    transformItems(category, items) {
      return items.map((item) => {
        switch (category) {
          case 'clientes':
            return {
              id: item.id,
              title: item.razaoSocial || item.nomeFantasia || 'Cliente sem nome',
              subtitle: `Código: ${item.codigoErp || item.id} | CNPJ: ${item.cnpjCpf || '-'}`,
              raw: item,
            };
          case 'pedidos':
            return {
              id: item.id,
              title: `Pedido #${item.prePedido}${item.pedidoErp ? ` (ERP: ${item.pedidoErp})` : ''}`,
              subtitle: item.clienteRazaoSocial || 'Cliente não informado',
              status: item.status,
              value: item.valorTotal,
              raw: item,
            };
          case 'rascunhos':
            return {
              id: item.id,
              title: `Rascunho #${item.prePedido}`,
              subtitle: `${item.clienteRazaoSocial || 'Cliente não informado'} - ${item.usuarioNome || ''}`,
              value: item.valorTotal,
              raw: item,
            };
          // case 'pics':
          //   return {
          //     id: item.id,
          //     title: `PIC #${item.picNumero}`,
          //     subtitle: item.clienteRazaoSocial || 'Cliente não informado',
          //     status: item.status,
          //     raw: item
          //   };
          case 'produtos':
            return {
              id: item.id,
              title: item.descricao || 'Produto sem descrição',
              subtitle: `Código: ${item.codigoErp || item.id}${item.marca ? ` | Marca: ${item.marca}` : ''}`,
              raw: item,
            };
          case 'representantes':
            return {
              id: item.id,
              title: item.nome || 'Representante sem nome',
              subtitle: `Código: ${item.codigoErp || item.id}${item.email ? ` | ${item.email}` : ''}`,
              raw: item,
            };
          default:
            return {
              id: item.id, title: 'Item', subtitle: '', raw: item,
            };
        }
      });
    },
    selectCategory(category) {
      this.selectedCategory = category;
      this.highlightedGroup = null;
      this.highlightedIndex = 0;
    },
    toggleExpand(groupKey) {
      this.$set(this.expandedGroups, groupKey, !this.expandedGroups[groupKey]);
    },
    highlightMatch(text) {
      if (!text || !this.query || this.query.length < 2) return text;

      const escapedQuery = this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return String(text).replace(regex, '<mark>$1</mark>');
    },
    formatCurrency(value) {
      const num = parseFloat(value) || 0;
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    getStatusClass(status) {
      if (!status) return '';
      const s = String(status).toLowerCase();
      if (s.includes('aprovado') || s.includes('faturado')) return 'status-success';
      if (s.includes('rascunho') || s.includes('pendente')) return 'status-warning';
      if (s.includes('cancelado') || s.includes('rejeitado')) return 'status-danger';
      return 'status-default';
    },
    navigateDown() {
      const groups = this.filteredResults;
      if (groups.length === 0) return;

      if (this.highlightedGroup === null) {
        this.highlightedGroup = groups[0].key;
        this.highlightedIndex = 0;
        return;
      }

      const currentGroupIndex = groups.findIndex((g) => g.key === this.highlightedGroup);
      const currentGroup = groups[currentGroupIndex];
      const maxIndex = Math.min(currentGroup.items.length - 1, this.expandedGroups[currentGroup.key] ? 19 : 4);

      if (this.highlightedIndex < maxIndex) {
        this.highlightedIndex++;
      } else if (currentGroupIndex < groups.length - 1) {
        this.highlightedGroup = groups[currentGroupIndex + 1].key;
        this.highlightedIndex = 0;
      }
    },
    navigateUp() {
      const groups = this.filteredResults;
      if (groups.length === 0 || this.highlightedGroup === null) return;

      const currentGroupIndex = groups.findIndex((g) => g.key === this.highlightedGroup);

      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      } else if (currentGroupIndex > 0) {
        const prevGroup = groups[currentGroupIndex - 1];
        this.highlightedGroup = prevGroup.key;
        this.highlightedIndex = Math.min(prevGroup.items.length - 1, this.expandedGroups[prevGroup.key] ? 19 : 4);
      }
    },
    selectCurrent() {
      if (this.highlightedGroup === null) return;
      const group = this.filteredResults.find((g) => g.key === this.highlightedGroup);
      if (group && group.items[this.highlightedIndex]) {
        this.selectItem(this.highlightedGroup, group.items[this.highlightedIndex]);
      }
    },
    isHighlighted(groupKey, index) {
      return this.highlightedGroup === groupKey && this.highlightedIndex === index;
    },
    setHighlight(groupKey, index) {
      this.highlightedGroup = groupKey;
      this.highlightedIndex = index;
    },
    selectItem(category, item) {
      const searchQuery = this.query;
      this.closeModal();

      const query = searchQuery ? { q: searchQuery } : undefined;

      switch (category) {
        case 'clientes':
          this.$router.push({
            name: 'apps-cliente-list',
            params: { id: item.id },
            query,
          }).catch(() => {});
          break;
        case 'pedidos':
          this.$router.push({
            name: 'pedidos-vendas-detalhes',
            params: { pedidoid: item.id, clienteid: item.raw.clienteId },
          }).catch(() => {});
          break;
        case 'rascunhos':
          this.$router.push({
            name: 'pedidos-vendas-detalhes',
            params: { pedidoid: item.id, clienteid: item.raw.clienteId },
          }).catch(() => {});
          break;
        // case 'pics':
        //   this.$router.push({
        //     name: 'operacoes-pics-detalhes',
        //     params: { id: item.id }
        //   }).catch(() => {});
        //   break;
        case 'produtos':
          this.$router.push({
            name: 'apps-item-list',
            params: { id: item.id },
            query,
          }).catch(() => {});
          break;
        case 'representantes':
          this.$router.push({
            name: 'apps-representante-list',
            params: { id: item.id },
            query,
          }).catch(() => {});
          break;
      }
    },
  },
};
</script>

<style scoped>
.global-smart-search {
  display: flex;
  align-items: center;
}

.search-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  height: 36px;
  background: var(--sf-header-control-bg, #ffffff);
  border: 1px solid var(--sf-header-control-border, #e2e8f0);
  border-radius: 8px;
  color: var(--sf-header-control-icon, #64748b);
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 200px;
}

.search-trigger:hover {
  background: var(--sf-header-control-hover-bg, #f8fafc);
  border-color: var(--sf-accent, #6366f1);
}

.search-label {
  flex: 1;
  text-align: left;
  font-size: 0.8125rem;
  color: var(--sf-header-control-icon, #94a3b8);
}

.keyboard-hint {
  font-size: 0.625rem;
  font-weight: 500;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: var(--sf-header-control-icon, #94a3b8);
  background: var(--sf-surface-bg, #f1f5f9);
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  border: 1px solid var(--sf-header-control-border, #e2e8f0);
}

.search-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 99999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 10vh 1rem;
}

.search-modal {
  width: 100%;
  max-width: 800px;
  max-height: 70vh;
  background: var(--sf-modal-bg, #ffffff);
  border: 1px solid var(--sf-border-color);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.search-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid var(--sf-border-color, #e5e7eb);
}

.search-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--sf-surface-secondary, #f8fafc);
  border: 1px solid var(--sf-border-color, #e5e7eb);
  border-radius: 8px;
}

.search-input-wrapper:focus-within {
  border-color: var(--sf-accent, #6366f1);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.search-icon {
  color: var(--sf-text-muted, #94a3b8);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 1rem;
  color: var(--sf-text-primary, #1e293b);
  outline: none;
}

.search-input::placeholder {
  color: var(--sf-text-muted, #94a3b8);
}

.loading-spinner {
  display: flex;
  align-items: center;
}

.clear-btn,
.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background: none;
  border: none;
  color: var(--sf-text-muted, #94a3b8);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.clear-btn:hover,
.close-btn:hover {
  color: var(--sf-text-primary, #1e293b);
  background: var(--sf-surface-secondary, #f1f5f9);
}

.search-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.categories-sidebar {
  width: 200px;
  flex-shrink: 0;
  padding: 0.5rem;
  border-right: 1px solid var(--sf-border-color, #e5e7eb);
  overflow-y: auto;
  background: var(--sf-surface-secondary, #f8fafc);
}

.category-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: var(--sf-text-secondary, #334155);
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.category-item:hover {
  background: var(--sf-surface-tertiary, #e2e8f0);
}

.category-item.active {
  background: var(--sf-accent, #6366f1);
  color: #ffffff;
}

.category-item.active .category-count {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.category-count {
  margin-left: auto;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  background: var(--sf-border-light, #e2e8f0);
  border-radius: 4px;
  color: var(--sf-text-muted, #64748b);
}

.category-divider {
  height: 1px;
  background: var(--sf-border-color, #e2e8f0);
  margin: 0.5rem 0;
}

.results-panel {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  background: var(--sf-modal-bg, #ffffff);
}

.results-loading {
  padding: 1rem;
}

.skeleton-group {
  margin-bottom: 1.5rem;
}

.skeleton-header {
  height: 14px;
  width: 100px;
  background: var(--sf-border-light, #e2e8f0);
  border-radius: 4px;
  margin-bottom: 0.75rem;
  animation: pulse 1.5s infinite;
}

.skeleton-item {
  height: 48px;
  background: var(--sf-surface-secondary, #f8fafc);
  border-radius: 6px;
  margin-bottom: 0.5rem;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.no-results,
.search-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: var(--sf-text-muted, #94a3b8);
}

.no-results-icon,
.hint-icon {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-results h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--sf-text-primary, #1e293b);
  margin-bottom: 0.25rem;
}

.no-results p,
.search-hint p {
  font-size: 0.875rem;
  margin: 0;
}

.hint-examples {
  margin-top: 1rem;
  font-size: 0.75rem;
  color: var(--sf-text-muted, #94a3b8);
}

.result-group {
  margin-bottom: 1.25rem;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--sf-text-muted, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.375rem;
}

.group-count {
  margin-left: auto;
  font-size: 0.625rem;
  padding: 0.0625rem 0.25rem;
  background: var(--sf-border-light, #e2e8f0);
  border-radius: 3px;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.1s ease;
}

.result-item:hover,
.result-item.is-highlighted {
  background: var(--sf-surface-secondary, #f1f5f9);
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--sf-surface-tertiary, #f1f5f9);
  border-radius: 6px;
  color: var(--sf-text-muted, #64748b);
  flex-shrink: 0;
}

.result-item.is-highlighted .item-icon,
.result-item:hover .item-icon {
  background: var(--sf-accent, #6366f1);
  color: #ffffff;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--sf-text-primary, #1e293b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-title :deep(mark) {
  background: var(--sf-accent-bg, rgba(99, 102, 241, 0.2));
  color: var(--sf-accent, #6366f1);
  padding: 0 0.125rem;
  border-radius: 2px;
}

.item-subtitle {
  font-size: 0.75rem;
  color: var(--sf-text-muted, #64748b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-subtitle :deep(mark) {
  background: var(--sf-accent-bg, rgba(99, 102, 241, 0.15));
  color: var(--sf-accent, #6366f1);
  padding: 0 0.125rem;
  border-radius: 2px;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.item-status {
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.1875rem 0.375rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.status-success {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-warning {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.status-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Dark mode overrides for status */
:global(body.dark-layout) .status-success {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}
:global(body.dark-layout) .status-warning {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}
:global(body.dark-layout) .status-danger {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.status-default {
  background: var(--sf-border-light, #e2e8f0);
  color: var(--sf-text-muted, #64748b);
}

.item-value {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--sf-text-secondary, #334155);
}

.item-arrow {
  color: var(--sf-text-muted, #94a3b8);
  opacity: 0;
  transition: opacity 0.1s ease;
}

.result-item:hover .item-arrow,
.result-item.is-highlighted .item-arrow {
  opacity: 1;
}

.see-more-btn {
  width: 100%;
  padding: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--sf-accent, #6366f1);
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.1s ease;
}

.see-more-btn:hover {
  background: var(--sf-surface-secondary, #f8fafc);
}

.search-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--sf-border-color, #e5e7eb);
  background: var(--sf-surface-secondary, #f8fafc);
}

.footer-hints {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
}

.hint {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  color: var(--sf-text-muted, #64748b);
}

.hint kbd {
  font-size: 0.625rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  padding: 0.125rem 0.25rem;
  background: var(--sf-modal-bg, #ffffff);
  border: 1px solid var(--sf-border-color, #e2e8f0);
  border-radius: 3px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .search-modal,
.modal-fade-leave-to .search-modal {
  transform: scale(0.95) translateY(-20px);
}

@media (max-width: 768px) {
  .search-trigger {
    min-width: auto;
    padding: 0.375rem;
  }

  .search-label,
  .keyboard-hint {
    display: none;
  }

  .search-modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .search-modal {
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
  }

  .search-body {
    flex-direction: column;
  }

  .categories-sidebar {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    display: flex;
    gap: 0.25rem;
    padding: 0.5rem;
    border-right: none;
    border-bottom: 1px solid var(--sf-border-color, #e5e7eb);
  }

  .category-item {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .category-divider {
    display: none;
  }
}
</style>
